import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { LucideAngularModule } from 'lucide-angular';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { AuthenticationService } from '../../services/authentication.service';
import { FriendsService } from '../../services/friends.service';
import { UserService } from '../../services/user.service';
import { FriendProfile, FriendshipStatus } from '../../model/friends';

export interface PartnerSelection {
  id: number;
  name: string;
}

@Component({
  selector: 'app-partner-picker',
  standalone: true,
  imports: [CommonModule, FormsModule, LucideAngularModule],
  templateUrl: './partner-picker.component.html',
  styleUrl: './partner-picker.component.css',
})
export class PartnerPickerComponent implements OnInit {
  @Output() partnerSelected = new EventEmitter<PartnerSelection>();

  friends: FriendProfile[] = [];
  searchResults: FriendProfile[] = [];
  searchTerm = '';
  searching = false;
  selected: PartnerSelection | null = null;

  private term$ = new Subject<string>();

  constructor(
    private auth: AuthenticationService,
    private friendsService: FriendsService,
    private userService: UserService,
  ) {}

  async ngOnInit(): Promise<void> {
    await this.auth.waitForInitialization();
    const userId = this.auth.currentUserValue?.id;
    if (userId != null) {
      this.friendsService.getFriendshipsForUser(userId).subscribe({
        next: friendships => {
          this.friends = friendships
            .filter(f => f.status === FriendshipStatus.Approved)
            .map(f => (f.friend_id === userId ? f.user : f.friend))
            .filter((p): p is FriendProfile => !!p);
        },
        error: () => { this.friends = []; },
      });
    }

    this.term$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap(term => {
          this.searching = true;
          return this.userService.searchUsers(term);
        }),
      )
      .subscribe({
        next: users => { this.searchResults = users ?? []; this.searching = false; },
        error: () => { this.searchResults = []; this.searching = false; },
      });
  }

  onSearchChange(term: string): void {
    this.searchTerm = term;
    if (term && term.trim().length >= 2) {
      this.term$.next(term.trim());
    } else {
      this.searchResults = [];
    }
  }

  displayName(p: FriendProfile): string {
    return p.displayName || [p.name, p.last_name].filter(Boolean).join(' ') || p.username || `Player ${p.id}`;
  }

  choose(p: FriendProfile): void {
    this.selected = { id: p.id, name: this.displayName(p) };
    this.partnerSelected.emit(this.selected);
  }

  clear(): void {
    this.selected = null;
  }
}
